import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import { safeJsonLd } from "./utils/safeJsonLd";
import {
  ArrowRight, Menu, X, Heart, Users, Stethoscope, Leaf,
  Apple, Shield, Globe, ChevronDown, Phone, Dumbbell,
  Sparkles, Target, UserCheck, MessageCircle, Activity,
  Facebook, Instagram, Youtube, Zap, FlaskConical, Scale,
  Salad, BedDouble, Cigarette
} from "lucide-react";

/* ─── Animation wrapper ─── */
const Anim = ({ children, className = "", delay = 0 }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.65, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}>{children}</motion.div>
  );
};

const WHATSAPP_PHONE = "919108818369";

const SectionHeading = ({ eyebrow, heading, children, light = false }) => (
  <Anim>
    <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
      {eyebrow && <span className={`text-xs tracking-[0.2em] uppercase font-semibold text-accent`}>{eyebrow}</span>}
      <h2 className={`font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight mt-3 ${light ? "text-bone" : "text-emerald_deep"}`}>{heading}</h2>
      {children && <div className={`mt-4 text-base leading-relaxed max-w-2xl mx-auto ${light ? "text-bone/70" : "text-text_muted"}`}>{children}</div>}
    </div>
  </Anim>
);

const Card = ({ icon: Icon, title, children }) => (
  <div className="bg-surface border border-border_custom p-6 md:p-8 rounded-xl hover:shadow-md transition-shadow h-full">
    {Icon && <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center mb-4"><Icon size={20} className="text-accent" /></div>}
    <h3 className="font-serif text-lg md:text-xl text-emerald_deep mb-3">{title}</h3>
    <div className="text-sm text-text_muted leading-relaxed">{children}</div>
  </div>
);

const FAQItem = ({ q, a, open, onToggle }) => (
  <div className="border-b border-border_custom">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-5 text-left group" aria-expanded={open}>
      <span className="font-serif text-base md:text-lg text-emerald_deep pr-4 group-hover:text-accent transition-colors">{q}</span>
      <ChevronDown size={18} className={`text-text_muted flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-accent" : ""}`} />
    </button>
    <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
      transition={{ duration: 0.3 }} className="overflow-hidden">
      <p className="text-sm text-text_muted leading-relaxed pb-5">{a}</p>
    </motion.div>
  </div>
);

/* ─── Header ─── */
const LandingHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  const navItems = [
    { label: "Home", href: "/" },
    { label: "Fertility Nutrition", href: "/fertility-nutrition" },
    { label: "Programs", href: "/#programs" },
    { label: "Team", href: "/#team" },
    { label: "Contact", href: "/#contact" }
  ];
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-bone/95 backdrop-blur-xl shadow-sm" : "bg-transparent"}`} data-testid="mf-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="font-serif text-2xl tracking-tight">
            <span className={`font-semibold ${scrolled ? "text-emerald_deep" : "text-bone"}`}>Fitly</span>
            <span className={`font-light ml-1 ${scrolled ? "text-text_muted" : "text-bone/60"}`}>India</span>
          </a>
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navItems.map(item => (
              <a key={item.label} href={item.href}
                className={`hover:text-accent transition-colors text-sm tracking-wide ${scrolled ? "text-text_muted" : "text-bone/70"}`}>{item.label}</a>
            ))}
          </nav>
          <div className="hidden md:block">
            <a href="/consultation" className="bg-accent text-emerald_deep px-7 py-2.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all inline-flex items-center gap-2 rounded-full">
              Book Consultation <ArrowRight size={14} />
            </a>
          </div>
          <button onClick={() => setMenuOpen(!menuOpen)} className={`md:hidden ${scrolled ? "text-emerald_deep" : "text-bone"}`}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden py-6 bg-surface border border-border_custom rounded-xl mt-2 mb-4 shadow-lg">
            {navItems.map(item => (
              <a key={item.label} href={item.href}
                className="block w-full text-left px-6 py-3 text-text_muted hover:text-emerald_deep hover:bg-bone/50 text-sm tracking-wide">{item.label}</a>
            ))}
            <div className="px-6 pt-3">
              <a href="/consultation" className="block w-full bg-emerald_deep text-bone py-3 text-sm tracking-wide text-center rounded-full">Book Consultation</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

/* ─── Footer ─── */
const LandingFooter = () => (
  <footer className="bg-emerald_deep text-bone py-16 relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div className="md:col-span-2">
          <div className="font-serif text-3xl mb-4"><span className="text-accent">Fitly</span> India</div>
          <p className="text-bone/60 leading-relaxed max-w-sm">Fertility Nutrition & Conception Care. Personalised online nutrition for women, men and couples across India and internationally.</p>
          <div className="flex gap-4 mt-6">
            {[[Facebook, "https://www.facebook.com/294056110466070"], [Instagram, "https://www.instagram.com/fitly_india"], [Youtube, "https://www.youtube.com/fitly_india"]].map(([Icon, href]) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-bone/10 flex items-center justify-center hover:bg-accent/20 transition-colors"><Icon size={16} className="text-bone/70" /></a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Quick Links</h4>
          <ul className="space-y-2">
            {[["Home", "/"], ["Fertility Nutrition", "/fertility-nutrition"], ["Male Fertility", "/male-fertility-nutrition"], ["Programs", "/#programs"], ["Team", "/#team"], ["Consultation", "/consultation"]].map(([label, href]) => (
              <li key={label}><a href={href} className="text-bone/60 hover:text-accent transition-colors text-sm">{label}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-bone/60">
            <li><a href="tel:+919108818369" className="hover:text-accent transition-colors">+91 91088 18369</a></li>
            <li><a href="mailto:fitlyindiahealth@gmail.com" className="hover:text-accent transition-colors">fitlyindiahealth@gmail.com</a></li>
            <li>Mon - Sat, 10 AM - 6 PM</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-bone/10 pt-8 text-center text-bone/40 text-xs">
        &copy; {new Date().getFullYear()} Fitly India. All rights reserved.
      </div>
    </div>
  </footer>
);

/* ═══ FAQ & Structured Data ═══ */

const faqData = [
  { q: "Can nutrition improve sperm quality?", a: "Nutrition plays a recognised role in supporting overall reproductive health in men. Correcting nutritional deficiencies, achieving a healthy weight, reducing oxidative stress and improving dietary patterns can positively influence sperm parameters. However, nutrition alone cannot resolve all causes of male infertility, and urological or andrological evaluation is recommended when needed." },
  { q: "What should a man eat to improve sperm count and motility?", a: "A balanced diet rich in antioxidants (vitamins C, E, selenium, zinc), omega-3 fatty acids, folate, adequate protein and a variety of fruits, vegetables and whole grains supports overall sperm health. Reducing processed foods, excess sugar, alcohol and trans fats is equally important. Fitly creates personalised plans rather than generic lists." },
  { q: "How long does it take for nutrition to affect sperm health?", a: "The sperm production cycle (spermatogenesis) takes approximately 72 to 90 days. This means nutritional and lifestyle changes made today can influence the sperm produced approximately three months later. Consistency over this window is important." },
  { q: "Can diet improve sperm morphology?", a: "While no specific food can guarantee improved morphology, a nutrient-dense diet supporting antioxidant status, reducing inflammation and correcting deficiencies creates a healthier environment for sperm development. Morphology concerns should also be discussed with a fertility specialist." },
  { q: "Does Fitly replace medical treatment for male infertility?", a: "No. Fitly's nutrition support works alongside medical care, not in place of it. If a semen analysis or clinical evaluation reveals a medical condition requiring treatment, Fitly encourages working with a qualified urologist or andrologist. Nutrition complements clinical management." },
  { q: "Do you provide male-only consultations?", a: "Yes. While Fitly offers couple programs, men can also join individually for a personalised nutrition plan focused on their reproductive and metabolic health." },
  { q: "Can lifestyle changes improve male fertility?", a: "Yes. Lifestyle factors including sleep quality, stress management, physical activity, weight management, and reducing alcohol, smoking and heat exposure to the testes can meaningfully influence sperm parameters alongside good nutrition." },
  { q: "What reports should I share before a male fertility consultation?", a: "A recent semen analysis is the most relevant report. Additionally, blood tests for hormones (testosterone, FSH, LH), vitamin D, B12, fasting insulin and a complete blood count can help Fitly tailor a more specific nutrition strategy. Share whatever reports you have — even partial information is useful." },
  { q: "Can my wife and I join together?", a: "Absolutely. Fitly's couple fertility programs provide personalised nutrition strategies for both partners, addressing female and male factors together. This is often the most effective approach since conception depends on both partners' health." },
  { q: "Is the male fertility consultation done online?", a: "Yes. All consultations are conducted online via video or voice call. Personalised plans are delivered digitally with ongoing support throughout the program. Clients from anywhere in India or internationally can join." }
];

const structuredData = {
  faq: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  },
  breadcrumb: {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fitlyindia.org/" },
      { "@type": "ListItem", "position": 2, "name": "Fertility Nutrition", "item": "https://fitlyindia.org/fertility-nutrition/" },
      { "@type": "ListItem", "position": 3, "name": "Male Fertility Nutrition", "item": "https://fitlyindia.org/male-fertility-nutrition/" }
    ]
  },
  service: {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Male Fertility Nutrition Consultation",
    "provider": {
      "@type": "Organization",
      "name": "Fitly India",
      "url": "https://fitlyindia.org",
      "telephone": "+919108818369",
      "email": "fitlyindiahealth@gmail.com",
      "address": { "@type": "PostalAddress", "addressLocality": "Bangalore", "addressCountry": "IN" }
    },
    "serviceType": "Male Fertility Nutrition Consultation",
    "areaServed": { "@type": "Country", "name": "India" },
    "description": "Personalised nutrition and lifestyle support for men's reproductive health. Sperm quality, metabolic health, preconception nutrition and IVF preparation for men."
  }
};

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════ */

export default function MaleFertilityNutritionPage() {
  const [openFaq, setOpenFaq] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-bone text-text_main" data-testid="male-fertility-page">
      <Helmet>
        <title>Male Fertility Nutritionist in India | Sperm Health & Diet | Fitly India</title>
        <meta name="description" content="Personalised male fertility nutrition for sperm health, motility, morphology and preconception care. Online male fertility nutritionist in India from Fitly India." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://fitlyindia.org/male-fertility-nutrition/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fitlyindia.org/male-fertility-nutrition/" />
        <meta property="og:title" content="Male Fertility Nutritionist in India | Fitly India" />
        <meta property="og:description" content="Personalised male fertility nutrition for sperm health, motility, morphology and preconception care. Online consultations from Fitly India." />
        <meta property="og:site_name" content="Fitly India" />
        <meta property="og:locale" content="en_IN" />
      </Helmet>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.faq) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJsonLd(structuredData.service) }} />

      <LandingHeader />

      <main>
        {/* ═══ HERO ═══ */}
        <section className="relative min-h-screen flex items-center bg-emerald_deep pt-20 overflow-hidden" data-testid="mf-hero">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 20%, #D4AF37 1px, transparent 1px)", backgroundSize: "60px 60px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full relative z-10 py-16 md:py-24">
            <div className="max-w-3xl">
              <Anim>
                <nav aria-label="Breadcrumb" className="mb-8">
                  <ol className="flex items-center gap-2 text-xs text-bone/40">
                    <li><a href="/" className="hover:text-accent transition-colors">Home</a></li>
                    <li>/</li>
                    <li><a href="/fertility-nutrition" className="hover:text-accent transition-colors">Fertility Nutrition</a></li>
                    <li>/</li>
                    <li className="text-accent">Male Fertility</li>
                  </ol>
                </nav>
              </Anim>
              <Anim delay={0.1}>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-bone mb-6">
                  Male Fertility Nutrition<br />
                  <span className="italic font-light text-accent">in India</span>
                </h1>
              </Anim>
              <Anim delay={0.2}>
                <p className="text-lg md:text-xl text-bone/80 leading-relaxed mb-6 max-w-2xl">
                  Personalised Nutrition & Lifestyle Support for Sperm Health, Preconception Wellness and Male Reproductive Health
                </p>
              </Anim>
              <Anim delay={0.3}>
                <p className="text-base text-bone/60 leading-relaxed mb-4">
                  Male factor contributes to approximately 40–50% of couple infertility, yet men's reproductive nutrition is often overlooked. At Fitly India, we believe male fertility deserves the same focused, personalised attention as female fertility.
                </p>
                <p className="text-base text-bone/60 leading-relaxed mb-4">
                  Our male fertility nutrition programs are designed around your health history, semen analysis findings, metabolic profile, nutritional status and lifestyle — not generic supplement lists.
                </p>
              </Anim>
              <Anim delay={0.4}>
                <div className="flex flex-wrap gap-3 mb-10">
                  {["Sperm Health", "Low Motility", "Low Count", "Morphology", "IVF Preparation", "Couple Programs"].map(tag => (
                    <span key={tag} className="border border-accent/30 text-accent text-xs tracking-wider px-4 py-2 rounded-full">{tag}</span>
                  ))}
                </div>
              </Anim>
              <Anim delay={0.5}>
                <a href="/consultation" data-testid="mf-hero-cta"
                  className="inline-flex items-center gap-2 bg-accent text-emerald_deep px-8 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                  Book a Male Fertility Consultation <ArrowRight size={16} />
                </a>
              </Anim>
            </div>
          </div>
        </section>

        {/* ═══ WHO IS THIS FOR ═══ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="mf-who">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Is This For You?" heading="Who Can Benefit From Male Fertility Nutrition?">
              <p>If any of these situations apply to you, personalised nutrition support can be a meaningful part of your journey.</p>
            </SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: FlaskConical, text: "Men with low sperm count or concentration concerns" },
                { icon: Zap, text: "Men with low sperm motility (slow or non-progressive movement)" },
                { icon: Target, text: "Men with abnormal sperm morphology findings" },
                { icon: Scale, text: "Men who are overweight or have metabolic health concerns" },
                { icon: Sparkles, text: "Men preparing for IVF, ICSI or other fertility treatments" },
                { icon: Users, text: "Couples pursuing preconception health together" },
                { icon: Leaf, text: "Men with nutritional deficiencies (vitamin D, zinc, B12, folate)" },
                { icon: Activity, text: "Men wanting to improve overall reproductive and metabolic health" },
                { icon: Shield, text: "Men who want a structured, evidence-informed nutrition strategy" }
              ].map((item, idx) => (
                <Anim key={item.text} delay={idx * 0.05}>
                  <div className="flex items-start gap-4 p-5 bg-surface border border-border_custom rounded-xl hover:shadow-sm transition-shadow">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <item.icon size={18} className="text-accent" />
                    </div>
                    <p className="text-sm text-emerald_deep leading-relaxed pt-2">{item.text}</p>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ SPERM HEALTH NUTRITION ═══ */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="mf-sperm-health">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 70% 30%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <SectionHeading eyebrow="Sperm Health" heading="Nutrition for Sperm Quality" light>
              <p>Sperm development takes approximately 72–90 days. This window is an opportunity to improve the nutritional environment in which sperm are produced.</p>
            </SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: FlaskConical, title: "Sperm Count & Concentration", text: "Nutrition addressing zinc, folate, antioxidant status and overall dietary quality to support healthy sperm production. Weight management and metabolic health are also key factors." },
                { icon: Zap, title: "Sperm Motility", text: "Targeted nutritional support for sperm movement including omega-3 fatty acids, CoQ10-supportive nutrition, L-carnitine-rich foods and antioxidant adequacy to reduce oxidative damage." },
                { icon: Target, title: "Sperm Morphology", text: "While morphology is influenced by many factors, a nutrient-rich diet minimising oxidative stress and inflammation creates a healthier environment for sperm development. Medical evaluation is always encouraged." },
                { icon: Shield, title: "Oxidative Stress & DNA Integrity", text: "Antioxidant-rich nutrition from whole foods — fruits, vegetables, nuts, seeds and healthy fats — helps combat oxidative stress that can damage sperm DNA and overall quality." },
                { icon: Scale, title: "Weight & Metabolic Health", text: "Excess weight, insulin resistance and metabolic syndrome are linked to reduced sperm quality. Fitly addresses these through personalised, sustainable nutrition strategies." },
                { icon: Leaf, title: "Nutritional Deficiencies", text: "Common deficiencies in vitamin D, zinc, selenium, B12 and folate can impact sperm parameters. Fitly identifies and addresses these through diet-first approaches with appropriate supplementation guidance." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.08}>
                  <div className="bg-bone/5 border border-bone/10 p-6 md:p-8 rounded-xl hover:bg-bone/10 transition-colors h-full">
                    <card.icon size={22} className="text-accent mb-4" />
                    <h3 className="font-serif text-lg text-bone mb-3">{card.title}</h3>
                    <p className="text-sm text-bone/65 leading-relaxed">{card.text}</p>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ LIFESTYLE FACTORS ═══ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="mf-lifestyle">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Beyond Diet" heading="Lifestyle Factors That Affect Male Fertility">
              <p>Nutrition is one part of the picture. Fitly addresses the broader lifestyle factors that influence male reproductive health.</p>
            </SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { icon: BedDouble, label: "Sleep Quality" },
                { icon: Activity, label: "Stress Management" },
                { icon: Dumbbell, label: "Exercise Balance" },
                { icon: Scale, label: "Weight Management" },
                { icon: Cigarette, label: "Smoking & Alcohol" },
                { icon: Salad, label: "Dietary Patterns" }
              ].map((item, idx) => (
                <Anim key={item.label} delay={idx * 0.06}>
                  <div className="text-center p-5 bg-surface border border-border_custom rounded-xl hover:shadow-sm transition-shadow">
                    <item.icon size={24} className="text-accent mx-auto mb-3" />
                    <p className="text-sm text-emerald_deep font-medium">{item.label}</p>
                  </div>
                </Anim>
              ))}
            </div>
            <Anim delay={0.4}>
              <p className="text-center text-sm text-text_muted mt-10 max-w-2xl mx-auto">
                These factors are woven into Fitly's male fertility programs — not as a lecture, but as practical, sustainable strategies that fit your real life.
              </p>
            </Anim>
          </div>
        </section>

        {/* ═══ COUPLE CONTEXT ═══ */}
        <section className="py-20 md:py-28 bg-surface_alt" data-testid="mf-couple">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Better Together" heading="Male Fertility Within the Couple Journey">
              <p>Conception depends on both partners. Fitly offers couple programs where male and female nutrition strategies work in parallel toward a shared goal.</p>
            </SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-10">
              <Anim>
                <div className="bg-surface border border-border_custom rounded-xl p-8 h-full">
                  <div className="w-12 h-12 rounded-full bg-emerald_deep/10 flex items-center justify-center mb-5">
                    <UserCheck size={22} className="text-emerald_deep" />
                  </div>
                  <h3 className="font-serif text-2xl text-emerald_deep mb-4">His Strategy</h3>
                  <p className="text-sm text-text_muted leading-relaxed">
                    Personalised nutrition based on semen analysis findings, metabolic health, nutritional gaps, lifestyle factors and individual health goals. A plan designed for his specific needs.
                  </p>
                </div>
              </Anim>
              <Anim delay={0.1}>
                <div className="bg-surface border border-border_custom rounded-xl p-8 h-full">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-5">
                    <Heart size={22} className="text-accent" />
                  </div>
                  <h3 className="font-serif text-2xl text-emerald_deep mb-4">Her Strategy</h3>
                  <p className="text-sm text-text_muted leading-relaxed">
                    Personalised nutrition addressing menstrual health, egg quality, hormonal balance, nutritional adequacy and preconception readiness. A complementary plan for her unique needs.
                  </p>
                </div>
              </Anim>
            </div>
            <Anim delay={0.2}>
              <p className="text-center font-serif text-xl md:text-2xl text-emerald_deep max-w-2xl mx-auto">
                Two personalised strategies. One shared goal — <span className="italic text-accent">better preconception health as a couple.</span>
              </p>
            </Anim>
            <Anim delay={0.3}>
              <div className="text-center mt-8">
                <a href="/fertility-nutrition" className="inline-flex items-center gap-2 text-accent text-sm font-medium hover:underline">
                  Learn about Fitly's full fertility nutrition approach <ArrowRight size={14} />
                </a>
              </div>
            </Anim>
          </div>
        </section>

        {/* ═══ IVF PREPARATION ═══ */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="mf-ivf">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 40% 60%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
            <SectionHeading eyebrow="Treatment Support" heading="Male Nutrition Before IVF or ICSI" light>
              <p>When a couple is preparing for IVF or ICSI, optimising male nutrition in the months before treatment can support sperm quality for the procedure.</p>
            </SectionHeading>
            <Anim>
              <div className="bg-bone/5 border border-bone/10 rounded-xl p-8 mb-10 text-left max-w-2xl mx-auto">
                <p className="text-sm text-bone/80 leading-relaxed mb-4">
                  Fitly's pre-treatment nutrition support for men focuses on antioxidant-rich nutrition, correcting identified deficiencies, weight optimisation where needed, and sustainable dietary habits.
                </p>
                <p className="text-sm text-bone/60 leading-relaxed">
                  All nutrition support works alongside — not in place of — your fertility specialist's treatment protocol. Medical decisions remain with the treating physician.
                </p>
              </div>
            </Anim>
            <Anim delay={0.1}>
              <a href="/consultation" className="inline-flex items-center gap-2 bg-accent text-emerald_deep px-8 py-3 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                Explore IVF Nutrition Support <ArrowRight size={14} />
              </a>
            </Anim>
          </div>
        </section>

        {/* ═══ WHY FITLY ═══ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="mf-why-fitly">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="The Fitly Difference" heading="Why Choose Fitly for Male Fertility Nutrition?" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: UserCheck, title: "Dedicated Male Programs", text: "Not an afterthought. Fitly has structured nutrition programs specifically for male reproductive health, not just female fertility programs with a male add-on." },
                { icon: Stethoscope, title: "Report-Based Approach", text: "Plans informed by semen analysis, blood work and metabolic markers — not guesswork or generic fertility food lists." },
                { icon: Target, title: "Personalised Strategy", text: "Every plan reflects individual dietary preferences, health history, lifestyle and nutritional requirements. No two clients receive identical recommendations." },
                { icon: Apple, title: "Indian Food. Real Life.", text: "Practical nutrition using familiar foods. No exotic fertility superfoods — just evidence-informed dietary strategies that work with your actual eating habits." },
                { icon: Users, title: "Couple Programs Available", text: "Join individually or as a couple. Couple programs address both partners simultaneously for a coordinated preconception approach." },
                { icon: Globe, title: "Online Across India", text: "Consultations via video or voice call with ongoing digital support. Available for men across India and internationally." }
              ].map((card, idx) => (
                <Anim key={card.title} delay={idx * 0.08}>
                  <Card icon={card.icon} title={card.title}>{card.text}</Card>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ HOW IT WORKS ═══ */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="mf-approach">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
            <SectionHeading eyebrow="The Process" heading="How Male Fertility Nutrition at Fitly Works" light />
            <div className="space-y-0">
              {[
                { num: "01", title: "Review", text: "Review your semen analysis, relevant blood work, medical history, current diet, lifestyle and fertility goals." },
                { num: "02", title: "Identify", text: "Identify nutritional gaps, metabolic concerns, lifestyle factors and areas where dietary changes can make a meaningful difference." },
                { num: "03", title: "Personalise", text: "Create a personalised nutrition strategy based on your specific findings — not a generic male fertility diet template." },
                { num: "04", title: "Support", text: "Ongoing guidance and adjustments through the program as you implement changes and progress toward your health goals." },
                { num: "05", title: "Collaborate", text: "Encourage appropriate medical evaluation when clinical management is needed. Nutrition supports — it does not replace — medical care." }
              ].map((step, idx) => (
                <Anim key={step.num} delay={idx * 0.1}>
                  <div className="flex gap-6 md:gap-10 py-8 border-b border-bone/10 group">
                    <span className="font-serif text-4xl md:text-5xl text-accent/30 group-hover:text-accent transition-colors flex-shrink-0">{step.num}</span>
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl text-bone mb-2 group-hover:text-accent transition-colors">{step.title}</h3>
                      <p className="text-sm text-bone/60 leading-relaxed max-w-xl">{step.text}</p>
                    </div>
                  </div>
                </Anim>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ TRUST ═══ */}
        <section className="py-20 md:py-28 bg-bone" data-testid="mf-trust">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <Anim>
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Medical Responsibility</span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-emerald_deep mt-3 mb-8">
                Nutrition Cannot Cure <span className="italic font-light">Male Infertility</span>
              </h2>
            </Anim>
            <Anim delay={0.1}>
              <div className="space-y-5 text-base text-text_muted leading-relaxed text-left">
                <p>Male infertility can have structural, hormonal, genetic or medical causes that require clinical evaluation and treatment by a qualified urologist or andrologist.</p>
                <p>No food, supplement or diet can guarantee improved fertility or pregnancy.</p>
                <p>Fitly's role is to help men optimise the nutritional and lifestyle factors realistically within their control — supporting overall reproductive health alongside appropriate medical care.</p>
                <p>If clinical investigation reveals a condition requiring medical intervention, Fitly encourages and supports working with the treating specialist.</p>
              </div>
            </Anim>
            <Anim delay={0.2}>
              <div className="gold-divider w-32 mx-auto mt-10"></div>
            </Anim>
          </div>
        </section>

        {/* ═══ FAQ ═══ */}
        <section className="py-20 md:py-28 bg-surface_alt" data-testid="mf-faq">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <SectionHeading eyebrow="Common Questions" heading="Male Fertility Nutrition FAQ" />
            <div className="bg-surface border border-border_custom rounded-xl p-6 md:p-8">
              {faqData.map((faq, idx) => (
                <FAQItem key={faq.q} q={faq.q} a={faq.a} open={openFaq === idx}
                  onToggle={() => setOpenFaq(openFaq === idx ? null : idx)} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══ FINAL CTA ═══ */}
        <section className="py-20 md:py-28 bg-emerald_deep relative" data-testid="mf-final-cta">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 60% 40%, #D4AF37 1px, transparent 1px)", backgroundSize: "50px 50px" }}></div>
          <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10 text-center">
            <Anim>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-bone mb-6">
                Your Fertility Journey<br />
                <span className="italic font-light text-accent">Deserves Focused Attention.</span>
              </h2>
            </Anim>
            <Anim delay={0.1}>
              <p className="text-base text-bone/70 leading-relaxed max-w-2xl mx-auto mb-10">
                Whether you're addressing a specific semen analysis concern, preparing for IVF, or simply want to optimise your preconception health, Fitly can help you build a personalised nutrition strategy for your journey.
              </p>
            </Anim>
            <Anim delay={0.2}>
              <div className="mb-10">
                <p className="font-serif text-2xl text-bone mb-1"><span className="text-accent">Fitly</span> India</p>
                <p className="text-sm text-bone/60 tracking-wider uppercase mb-1">Male Fertility Nutrition</p>
                <p className="text-xs text-accent tracking-wider">For Him. For Couples. For the Journey Ahead.</p>
              </div>
            </Anim>
            <Anim delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/consultation" data-testid="mf-final-cta-book"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-emerald_deep px-10 py-3.5 text-sm tracking-wide font-semibold hover:bg-accent_light transition-all rounded-full">
                  Book Your Consultation <ArrowRight size={16} />
                </a>
                <a href={`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent("Hi! I'd like to learn more about Fitly India's male fertility nutrition programs.")}`}
                  target="_blank" rel="noopener noreferrer" data-testid="mf-final-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 border border-bone/20 text-bone px-8 py-3.5 text-sm tracking-wide hover:bg-bone/10 transition-all rounded-full">
                  <MessageCircle size={16} /> Message on WhatsApp
                </a>
              </div>
            </Anim>
          </div>
        </section>

        {/* ═══ DISCLAIMER ═══ */}
        <aside className="py-10 bg-bone border-t border-border_custom" data-testid="mf-disclaimer">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <p className="text-xs text-text_muted/70 leading-relaxed text-center">
              <strong className="text-text_muted">Medical Disclaimer:</strong> Information provided on this page is for educational purposes and does not constitute diagnosis or medical treatment. Male fertility conditions require appropriate evaluation by qualified healthcare professionals including urologists or andrologists. Nutrition and lifestyle interventions cannot guarantee improved fertility parameters or conception and should not replace medical treatment. Fitly India encourages clients to work with qualified medical professionals for clinical fertility care.
            </p>
          </div>
        </aside>
      </main>

      <LandingFooter />
    </div>
  );
}
